import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { 
  Truck, Plus, FileText, Calendar, User, Package, 
  CheckCircle, Clock, Trash2, Loader2, AlertTriangle
} from 'lucide-react';
import { formatDateDisplay } from '@/utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function EntregasTab({ order, onUpdate }) {
  const [entregas, setEntregas] = useState([]);
  const [resumoProdutos, setResumoProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNovaEntrega, setShowNovaEntrega] = useState(false);
  const [salvando, setSalvando] = useState(false);
  
  // Estado do formulário de nova entrega
  const [novaEntrega, setNovaEntrega] = useState({
    entregador: '',
    data_entrega: new Date().toISOString().split('T')[0],
    observacoes: '',
    itens: []
  });

  // Carregar entregas
  const fetchEntregas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/orders/${order.id}/entregas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntregas(response.data.entregas || []);
      setResumoProdutos(response.data.resumo_produtos || []);
    } catch (error) {
      console.error('Erro ao carregar entregas:', error);
      toast.error('Erro ao carregar entregas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (order?.id) {
      fetchEntregas();
    }
  }, [order?.id]);

  // Inicializar itens do formulário quando abrir o modal
  const abrirNovaEntrega = () => {
    const itensDisponiveis = resumoProdutos
      .filter(p => p.quantidade_pendente > 0)
      .map(p => ({
        produto_id: p.produto_id,
        variacao_id: p.variacao_id || '',
        nome: p.nome,
        quantidade_pedida: p.quantidade_pedida,
        quantidade_pendente: p.quantidade_pendente,
        quantidade_entregue: p.quantidade_pendente, // Default: entregar tudo que falta
        selecionado: true
      }));
    
    setNovaEntrega({
      entregador: '',
      data_entrega: new Date().toISOString().split('T')[0],
      observacoes: '',
      itens: itensDisponiveis
    });
    setShowNovaEntrega(true);
  };

  // Registrar entrega
  const registrarEntrega = async () => {
    const itensSelecionados = novaEntrega.itens.filter(i => i.selecionado && i.quantidade_entregue > 0);
    
    if (itensSelecionados.length === 0) {
      toast.error('Selecione pelo menos um item para entregar');
      return;
    }
    
    if (!novaEntrega.entregador.trim()) {
      toast.error('Informe o nome do entregador');
      return;
    }

    setSalvando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/orders/${order.id}/entregas`, {
        itens: itensSelecionados.map(i => ({
          produto_id: i.produto_id,
          variacao_id: i.variacao_id || null,
          nome: i.nome,
          quantidade_entregue: parseInt(i.quantidade_entregue) || 0
        })),
        entregador: novaEntrega.entregador,
        data_entrega: novaEntrega.data_entrega,
        observacoes: novaEntrega.observacoes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Entrega registrada! Romaneio #${response.data.numero_romaneio}`);
      setShowNovaEntrega(false);
      fetchEntregas();
      
      // Gerar PDF do romaneio
      gerarRomaneioPDF(response.data.numero_romaneio, itensSelecionados, novaEntrega);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 
                      Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') :
                      'Erro ao registrar entrega';
      toast.error(message);
    } finally {
      setSalvando(false);
    }
  };

  // Gerar PDF do Romaneio
  const gerarRomaneioPDF = (numeroRomaneio, itens, dadosEntrega) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Permita pop-ups para gerar o PDF');
      return;
    }

    const dataFormatada = formatDateDisplay(dadosEntrega.data_entrega);
    const agora = new Date().toLocaleString('pt-BR');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Romaneio de Entrega #${numeroRomaneio}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            font-size: 24px;
            margin-bottom: 5px;
          }
          .header .romaneio-num {
            font-size: 18px;
            color: #666;
          }
          .info-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            padding: 15px;
            background: #f5f5f5;
            border-radius: 5px;
          }
          .info-block h3 {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
            text-transform: uppercase;
          }
          .info-block p {
            font-size: 14px;
            font-weight: bold;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .items-table th {
            background: #333;
            color: white;
            padding: 10px;
            text-align: left;
            font-size: 12px;
          }
          .items-table td {
            padding: 10px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
          }
          .items-table tr:nth-child(even) {
            background: #f9f9f9;
          }
          .observacoes {
            padding: 15px;
            background: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 5px;
            margin-bottom: 30px;
          }
          .observacoes h4 {
            font-size: 12px;
            color: #856404;
            margin-bottom: 5px;
          }
          .signature-section {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px dashed #ccc;
          }
          .signature-box {
            display: flex;
            justify-content: space-between;
            margin-top: 30px;
          }
          .signature-field {
            width: 45%;
            text-align: center;
          }
          .signature-line {
            border-bottom: 1px solid #333;
            height: 60px;
            margin-bottom: 10px;
          }
          .signature-label {
            font-size: 12px;
            color: #666;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #999;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ROMANEIO DE ENTREGA</h1>
          <div class="romaneio-num">Nº ${String(numeroRomaneio).padStart(4, '0')} - Pedido #${order.numero_pedido || order.id.slice(0, 8)}</div>
        </div>

        <div class="info-section">
          <div class="info-block">
            <h3>Cliente</h3>
            <p>${order.cliente_nome || 'Cliente não informado'}</p>
            ${order.cliente_telefone ? `<p style="font-weight: normal; font-size: 12px;">${order.cliente_telefone}</p>` : ''}
            ${order.cliente_endereco ? `<p style="font-weight: normal; font-size: 11px; max-width: 250px;">${order.cliente_endereco}</p>` : ''}
          </div>
          <div class="info-block">
            <h3>Data da Entrega</h3>
            <p>${dataFormatada}</p>
          </div>
          <div class="info-block">
            <h3>Entregador</h3>
            <p>${dadosEntrega.entregador}</p>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 50%">Produto</th>
              <th style="width: 25%; text-align: center;">Quantidade</th>
              <th style="width: 25%; text-align: center;">Conferido</th>
            </tr>
          </thead>
          <tbody>
            ${itens.map(item => `
              <tr>
                <td>${item.nome}</td>
                <td style="text-align: center; font-weight: bold;">${item.quantidade_entregue}</td>
                <td style="text-align: center;">☐</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        ${dadosEntrega.observacoes ? `
          <div class="observacoes">
            <h4>Observações:</h4>
            <p>${dadosEntrega.observacoes}</p>
          </div>
        ` : ''}

        <div class="signature-section">
          <p style="font-size: 12px; color: #666; margin-bottom: 10px;">
            Declaro que recebi os produtos acima em perfeito estado.
          </p>
          <div class="signature-box">
            <div class="signature-field">
              <div class="signature-line"></div>
              <div class="signature-label">Assinatura do Cliente</div>
            </div>
            <div class="signature-field">
              <div class="signature-line"></div>
              <div class="signature-label">Nome Legível / CPF</div>
            </div>
          </div>
        </div>

        <div class="footer">
          <p>Documento gerado em ${agora}</p>
          <p>Serraria Guedes - Sistema de Gestão</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Excluir entrega
  const excluirEntrega = async (entregaId) => {
    if (!window.confirm('Deseja realmente excluir esta entrega?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/orders/${order.id}/entregas/${entregaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entrega excluída');
      fetchEntregas();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir entrega');
    }
  };

  // Reimprimir romaneio
  const reimprimirRomaneio = (entrega) => {
    gerarRomaneioPDF(
      entrega.numero_romaneio,
      entrega.itens,
      {
        entregador: entrega.entregador,
        data_entrega: entrega.data_entrega,
        observacoes: entrega.observacoes
      }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Calcular progresso geral
  const totalPedido = resumoProdutos.reduce((acc, p) => acc + p.quantidade_pedida, 0);
  const totalEntregue = resumoProdutos.reduce((acc, p) => acc + p.quantidade_entregue, 0);
  const progressoGeral = totalPedido > 0 ? (totalEntregue / totalPedido) * 100 : 0;
  const temPendentes = resumoProdutos.some(p => p.quantidade_pendente > 0);

  return (
    <div className="space-y-4">
      {/* Resumo do Progresso */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            Progresso das Entregas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {totalEntregue} de {totalPedido} itens entregues
              </span>
              <Badge variant={progressoGeral === 100 ? 'default' : 'secondary'} className={progressoGeral === 100 ? 'bg-emerald-500' : ''}>
                {progressoGeral.toFixed(0)}%
              </Badge>
            </div>
            <Progress value={progressoGeral} className="h-2" />
            
            {/* Lista de produtos com status */}
            <div className="mt-4 space-y-2">
              {resumoProdutos.map((produto, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    {produto.entregue_completo ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Clock className="h-4 w-4 text-amber-500" />
                    )}
                    <span className={produto.entregue_completo ? 'text-muted-foreground line-through' : ''}>
                      {produto.nome}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-xs ${produto.entregue_completo ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {produto.quantidade_entregue}/{produto.quantidade_pedida}
                    </span>
                    {produto.quantidade_pendente > 0 && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                        Falta: {produto.quantidade_pendente}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Botão Nova Entrega */}
      {temPendentes && (
        <Button onClick={abrirNovaEntrega} className="w-full" data-testid="btn-nova-entrega">
          <Plus className="h-4 w-4 mr-2" />
          Registrar Nova Entrega
        </Button>
      )}

      {/* Lista de Entregas Realizadas */}
      {entregas.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Entregas Realizadas ({entregas.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {entregas.map((entrega, idx) => (
              <div key={entrega.id} className="p-3 bg-muted/50 rounded-lg border">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="font-mono">
                        Romaneio #{String(entrega.numero_romaneio).padStart(4, '0')}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDateDisplay(entrega.data_entrega)}
                      </span>
                    </div>
                    <div className="text-sm flex items-center gap-1 text-muted-foreground">
                      <User className="h-3 w-3" />
                      {entrega.entregador}
                    </div>
                    <div className="mt-2 text-xs space-y-1">
                      {entrega.itens.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Package className="h-3 w-3 text-primary" />
                          <span>{item.quantidade_entregue}x {item.nome}</span>
                        </div>
                      ))}
                    </div>
                    {entrega.observacoes && (
                      <p className="mt-2 text-xs text-muted-foreground italic">
                        &quot;{entrega.observacoes}&quot;
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => reimprimirRomaneio(entrega)}
                      title="Reimprimir Romaneio"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => excluirEntrega(entrega.id)}
                      title="Excluir Entrega"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Modal Nova Entrega */}
      <Dialog open={showNovaEntrega} onOpenChange={setShowNovaEntrega}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              Nova Entrega
            </DialogTitle>
            <DialogDescription>
              Pedido #{order.numero_pedido || order.id.slice(0, 8)} - {order.cliente_nome}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Dados da Entrega */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="entregador">Entregador *</Label>
                <Input
                  id="entregador"
                  placeholder="Nome do entregador"
                  value={novaEntrega.entregador}
                  onChange={(e) => setNovaEntrega({ ...novaEntrega, entregador: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_entrega">Data da Entrega</Label>
                <Input
                  id="data_entrega"
                  type="date"
                  value={novaEntrega.data_entrega}
                  onChange={(e) => setNovaEntrega({ ...novaEntrega, data_entrega: e.target.value })}
                />
              </div>
            </div>

            {/* Itens a Entregar */}
            <div className="space-y-2">
              <Label>Itens a Entregar</Label>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {novaEntrega.itens.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${item.selecionado ? 'bg-primary/5 border-primary/30' : 'bg-muted/30'}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={item.selecionado}
                        onCheckedChange={(checked) => {
                          const novosItens = [...novaEntrega.itens];
                          novosItens[idx].selecionado = checked;
                          setNovaEntrega({ ...novaEntrega, itens: novosItens });
                        }}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{item.nome}</div>
                        <div className="text-xs text-muted-foreground">
                          Pendente: {item.quantidade_pendente} un
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Qtd:</Label>
                        <Input
                          type="number"
                          min="1"
                          max={item.quantidade_pendente}
                          value={item.quantidade_entregue}
                          onChange={(e) => {
                            const novosItens = [...novaEntrega.itens];
                            novosItens[idx].quantidade_entregue = Math.min(
                              parseInt(e.target.value) || 0,
                              item.quantidade_pendente
                            );
                            setNovaEntrega({ ...novaEntrega, itens: novosItens });
                          }}
                          className="w-20 h-8"
                          disabled={!item.selecionado}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações sobre a entrega (opcional)"
                value={novaEntrega.observacoes}
                onChange={(e) => setNovaEntrega({ ...novaEntrega, observacoes: e.target.value })}
                rows={2}
              />
            </div>

            {/* Aviso */}
            <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5" />
              <p className="text-xs text-blue-700">
                Ao salvar, será gerado um <strong>Romaneio de Entrega</strong> para impressão, 
                com campo de assinatura do cliente.
              </p>
            </div>

            {/* Botões */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNovaEntrega(false)} disabled={salvando}>
                Cancelar
              </Button>
              <Button onClick={registrarEntrega} disabled={salvando}>
                {salvando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Salvar e Gerar Romaneio
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
